import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, DollarSign, MessageSquare, Wallet, CheckCircle2, XCircle, Clock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MONTHS = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const fmt = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const YEAR = new Date().getFullYear();

const STATUS_ICON = { open: Clock, in_progress: ShieldCheck, resolved: CheckCircle2 };
const STATUS_LABEL = { open: 'Terbuka', in_progress: 'Diproses', resolved: 'Terselesaikan' };
const STATUS_CLS = { open: 'bg-amber-100 text-amber-700', in_progress: 'bg-blue-100 text-blue-700', resolved: 'bg-green-100 text-green-700' };

export default function CitizenPortalPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { navigate('/login'); return; }
        axios.get(`${API}/citizen-portal/dashboard`, { withCredentials: true })
            .then(r => setData(r.data))
            .catch(() => toast.error('Gagal memuat data'))
            .finally(() => setLoading(false));
    }, [user]);

    const handleLogout = async () => {
        await logout();
        toast.success('Berhasil keluar');
        navigate('/');
    };

    if (!user) return null;

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Memuat data warga...</p>
                </div>
            </div>
        );
    }

    const citizen = data?.citizen;
    const iuranData = data?.iuran_data || [];
    const complaints = data?.complaints || [];
    const finance = data?.finance_summary || {};

    const paidMonths = iuranData.map(i => i.month);

    return (
        <div className="min-h-screen" style={{ background: 'hsl(210 40% 98%)' }} data-testid="citizen-portal">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <a href="/" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors">
                            <ArrowLeft className="w-4 h-4" />
                        </a>
                        <div>
                            <p className="font-extrabold text-slate-900 text-sm leading-none" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                Forum Warga
                            </p>
                            <p className="text-xs text-slate-500">Portal Warga RT</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="text-right mr-1 hidden sm:block">
                            <p className="text-sm font-semibold text-slate-800">{user.name}</p>
                            <p className="text-xs text-slate-400">Warga</p>
                        </div>
                        <button onClick={handleLogout} data-testid="warga-logout-btn"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors font-medium">
                            <LogOut className="w-4 h-4" />
                            <span className="hidden sm:inline">Keluar</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
                {/* Identity Card */}
                {citizen ? (
                    <div className="rounded-2xl overflow-hidden shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 55%, #0F766E 100%)' }}>
                        <div className="p-6 text-white">
                            <p className="text-xs text-white/60 tracking-widest uppercase mb-3">Kartu Iuran Digital</p>
                            <h2 className="text-2xl font-extrabold mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                {citizen.head_of_family}
                            </h2>
                            <p className="text-white/70 text-sm font-mono">KK: {citizen.kk_number}</p>
                            <p className="text-white/60 text-sm mt-1">{citizen.address}</p>
                            <p className="text-white/50 text-xs mt-1">{citizen.members?.length || 0} anggota keluarga</p>

                            {/* Monthly iuran grid */}
                            <div className="mt-4">
                                <p className="text-xs text-white/60 mb-2">Status Iuran {YEAR}</p>
                                <div className="grid grid-cols-6 gap-1.5">
                                    {MONTHS.slice(1).map((mn, i) => {
                                        const paid = paidMonths.includes(i + 1);
                                        return (
                                            <div key={i} className={`rounded-lg p-1.5 text-center text-xs font-bold ${paid ? 'bg-white/30' : 'bg-white/10'}`}>
                                                <div>{mn.substring(0, 3)}</div>
                                                <div>{paid ? '✓' : '—'}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                            <p className="font-bold text-slate-800 text-lg">{user.name}</p>
                            <p className="text-sm text-slate-500">Akun warga belum ditautkan ke data KK</p>
                            <p className="text-xs text-slate-400 mt-1">Hubungi petugas RT untuk menautkan akun Anda</p>
                        </div>
                    </div>
                )}

                {/* Finance Summary */}
                <div>
                    <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                        <DollarSign className="w-5 h-5 text-primary" /> Kas RT Bulan Ini
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Pemasukan Iuran', value: fmt(finance.iuran_total), color: 'text-green-700' },
                            { label: 'Pengeluaran', value: fmt(finance.expense_total), color: 'text-orange-600' },
                            { label: 'Saldo Kas', value: fmt(finance.balance), color: finance.balance >= 0 ? 'text-teal-700' : 'text-red-600' },
                        ].map(item => (
                            <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                                <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                                <p className={`font-extrabold text-sm ${item.color}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                    {item.value}
                                </p>
                            </div>
                        ))}
                    </div>
                    <a href="/public" className="mt-3 flex items-center justify-center gap-2 text-sm text-primary font-semibold hover:underline">
                        Lihat laporan keuangan lengkap →
                    </a>
                </div>

                {/* Iuran History */}
                {iuranData.length > 0 && (
                    <div>
                        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            <Wallet className="w-5 h-5 text-primary" /> Riwayat Iuran {YEAR}
                        </h3>
                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                            {iuranData.map((i, idx) => (
                                <div key={i.id} className={`flex items-center justify-between px-5 py-3.5 ${idx < iuranData.length - 1 ? 'border-b border-slate-100' : ''}`}>
                                    <div className="flex items-center gap-3">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span className="font-medium text-slate-700 text-sm">{MONTHS[i.month]} {i.year}</span>
                                    </div>
                                    <span className="font-bold text-green-700 text-sm">{fmt(i.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Complaints */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            <MessageSquare className="w-5 h-5 text-primary" /> Pengaduan Saya
                        </h3>
                        <a href="/complaints" className="text-xs text-primary font-semibold hover:underline">
                            Buat Pengaduan →
                        </a>
                    </div>
                    {complaints.length === 0 ? (
                        <div className="bg-white rounded-xl border border-slate-200 py-10 text-center">
                            <MessageSquare className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                            <p className="text-slate-400 text-sm">Belum ada pengaduan</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {complaints.map(c => {
                                const Icon = STATUS_ICON[c.status] || Clock;
                                return (
                                    <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-slate-800 text-sm">{c.title}</p>
                                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.description}</p>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    {new Date(c.created_at).toLocaleDateString('id-ID')}
                                                </p>
                                            </div>
                                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${STATUS_CLS[c.status] || STATUS_CLS.open}`}>
                                                {STATUS_LABEL[c.status] || 'Terbuka'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="text-center pb-6">
                    <p className="text-xs text-slate-400">Sistem Informasi RT Digital &mdash; Forum Warga</p>
                </div>
            </div>
        </div>
    );
}
